/* eslint-disable */
import { ref, computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useToast } from "primevue/usetoast";
import { useRoute } from "vue-router";
import {
  fetchUserSubscription,
  fetchOrganization,
  fetchUsersStats,
  fetchMembers,
  fetchModulePluzzStats,
  downloadStats,
} from "@/services/userFollowService";

export function useUserFollow() {
  const { t } = useI18n();
  const toast = useToast();
  const route = useRoute();
  const activeTab = ref(route.fullPath);
  // State
  const loading = ref(false);
  const isDownloading = ref(false);
  const users = ref([]);
  const usersStats = ref({
    users_started: 0,
    users_over_50: 0,
    users_completed: 0,
    users_certified: 0,
  });

  const modulePluzzStats = ref({
    users_started_pluzz: 0,
    users_over_50_pluzz: 0,
    users_less_50_pluzz: 0,
    users_completed_pluzz: 0,
  });

  const searchTerm = ref("");
  const subscription = ref(null);
  const organization = ref(null);
  // Computed
  const filteredUsers = computed(() => {
    if (!searchTerm.value) return users.value;

    const term = searchTerm.value.toLowerCase();
    return users.value.filter(
      (user) =>
        user.fullName?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        user.service?.name?.toLowerCase().includes(term)
      //|| user.phone?.includes(term)
    );
  });

  // Methods
  const retrieveUserSubscription = async () => {
    try {
      const response = await fetchUserSubscription();
      if (response && response.success) {
        subscription.value = response.data;
        return response.data;
      }
    } catch (error) {
      console.error("Error retrieving user subscription:", error);
      toast.add({
        severity: "error",
        summary: t("error.title"),
        detail: t("error.subscription_fetch"),
        life: 3000,
      });
    }
    return null;
  };

  const getOrganizationData = async () => {
    try {
      const response = await fetchOrganization();
      if (response && response.success) {
        organization.value = response.data;
        return response.data;
      }
    } catch (error) {
      console.error("Error fetching organization data:", error);
      toast.add({
        severity: "error",
        summary: t("error.title"),
        detail: t("error.organization_fetch"),
        life: 3000,
      });
    }
    return null;
  };

  const getUsersStats = async () => {
    try {
      const response = await fetchUsersStats();
      if (response && response.success) {
        usersStats.value = {
          users_started: response.data?.total_user_start_module || 0,
          users_over_50: response.data?.total_user_more_50_progression || 0,
          users_less_50: response.data?.total_user_less_50_progression || 0,
          users_completed: response.data?.total_user_complete_module || 0,
          users_certified: response.data?.total_user_certificated || 0,
          total_users: response.data?.user_data.length || 0,
        };
      }
    } catch (error) {
      console.error("Error fetching users stats:", error);
      toast.add({
        severity: "error",
        summary: t("error.title"),
        detail: t("error.stats_fetch"),
        life: 3000,
      });
    }
  };

  const getModulePluzzStats = async () => {
    try {
      const response = await fetchModulePluzzStats();
      if (response && response.success) {
        modulePluzzStats.value = {
          users_started_pluzz: response.data?.total_user_start_module || 0,
          users_over_50_pluzz:
            response.data?.total_user_more_50_progression || 0,
          users_less_50_pluzz:
            response.data?.total_user_less_50_progression || 0,
          users_completed_pluzz: response.data?.total_user_complete_module || 0,
          total_users: response.data?.user_data.length || 0,
        };
      }
    } catch (error) {
      console.error("Error fetching module pluzz stats:", error);
      toast.add({
        severity: "error",
        summary: t("error.title"),
        detail: t("error.stats_fetch"),
        life: 3000,
      });
    }
  };

  const getMembers = async () => {
    try {
      loading.value = true;
      const response = await fetchMembers();

      if (response && response.success) {
        const membersData = response.data || [];
        users.value = membersData.map((member) => buildUserData(member));
      }
    } catch (error) {
      console.error("Error fetching members:", error);
      toast.add({
        severity: "error",
        summary: t("error.title"),
        detail: t("error.members_fetch"),
        life: 3000,
      });
    } finally {
      loading.value = false;
    }
  };

  const buildUserData = (member) => {
    const fullName = `${member.firstname || ""} ${
      member.lastname || ""
    }`.trim();
    const avatar = member.avatar || "/images/defaultImage.png";
    console.log(activeTab.value, "here check active tab");
    const isPluzz = activeTab.value === "/user-follow#pluzz";
    // Calculate progression
    // Calculate progression and cyberscore from stat_module
    let progressionSum = 0;
    let cyberscoreSum = 0;
    const statModule = isPluzz
      ? member.extra_data?.stat_module_pluzz || []
      : member.extra_data?.stat_module || [];

    if (statModule.length > 0) {
      for (const module of statModule) {
        progressionSum += module.progression || 0;
        cyberscoreSum += module.cyberscore || 0;
      }
    }

    const progressionValue = Math.round(
      progressionSum / (statModule.length || 1)
    );
    let raw_cyberscore = Math.round(cyberscoreSum / (statModule.length || 1));
    const cyberscore = `${raw_cyberscore}/100`;

    // Calculate certification from stat_quizz
    let certificationValue = 0;
    const statQuizz = member.extra_data?.stat_quizz || [];

    if (statQuizz.length > 0) {
      certificationValue = Math.round(
        statQuizz.reduce((sum, quiz) => sum + (quiz.note || 0), 0) /
          statQuizz.length
      );
    }

    const certification = `${certificationValue}/100`;

    // Determine status
    let status = t("user_progression.status_inactive");
    let statusType = "inactive";

    if (member.status === "active") {
      status = t("user_progression.status_active");
      statusType = "active";
    } else if (member.status === "pending") {
      status = t("user_progression.status_pending");
      statusType = "pending";
    }

    // Format phone number
    const phone = validatePhoneNumber(member.phone) ? member.phone : "";

    // Get start and completion dates
    let startDate = null;
    let completionDate = null;

    if (statModule.length > 0) {
      startDate = statModule[0]?.created_at || null;
    }

    if (
      statQuizz.length > 0 &&
      statQuizz[0]?.note > 0 &&
      statModule.length > 0
    ) {
      completionDate = statModule[0]?.updated_at || null;
    }

    return {
      id: member.id,
      fullName,
      avatar,
      service: member.service || t("common.not_assigned"),
      status,
      statusType,
      email: member.email || "",
      phone,
      cyberscore,
      rawCyberscore: raw_cyberscore,
      progression: `${progressionValue}%`,
      progressionValue,
      certification,
      rawCertification: certificationValue,
      registrationDate: member.created_at || member.registration_date,
      startDate,
      completionDate,
      role: member?.role,
    };
  };

  const validatePhoneNumber = (phone) => {
    if (!phone) return false;
    // Basic phone validation - adjust regex as needed
    const phoneRegex = /^[+]?[0-9\s\-\(\)]{8,}$/;
    return phoneRegex.test(phone);
  };

  const formatDate = (dateString) => {
    if (!dateString) return t("common.not_available");

    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("fr-FR", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    } catch (error) {
      return t("common.invalid_date");
    }
  };

  const getStatusSeverity = (statusType) => {
    switch (statusType) {
      case "active":
        return "success";
      case "pending":
        return "warning";
      case "inactive":
      default:
        return "danger";
    }
  };

  const loadUsers = async () => {
    await getMembers();
  };

  const loadUsersStats = async () => {
    await getUsersStats();
    await getModulePluzzStats();
  };

  const refreshData = async (isAlert = false) => {
    loading.value = true;
    try {
      await Promise.all([
        loadUsers(),
        loadUsersStats(),
        retrieveUserSubscription(),
        getOrganizationData(),
      ]);
      if (isAlert) {
        toast.add({
          severity: "success",
          summary: t("common.success_title"),
          detail: t("common.success_data_refreshed"),
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error refreshing data:", error);
      toast.add({
        severity: "error",
        summary: t("error.title"),
        detail: t("error.refresh_failed"),
        life: 3000,
      });
    } finally {
      loading.value = false;
    }
  };

  const downloadReportPdf = async (moduleType) => {
    isDownloading.value = true;
    try {
      const blob = await downloadStats("pdf", moduleType);
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "rapport_statistiques.pdf");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.add({
          severity: "success",
          summary: t("common.success_title"),
          detail: "Rapport téléchargé avec succès",
          life: 3000,
        });
      } else {
        throw new Error("No data received");
      }
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.add({
        severity: "error",
        summary: t("error.title"),
        detail: "Erreur lors du téléchargement du rapport",
        life: 3000,
      });
    } finally {
      isDownloading.value = false;
    }
  };

  const downloadReportExcel = async (moduleType) => {
    isDownloading.value = true;
    try {
      const blob = await downloadStats("excel", moduleType);
      if (blob) {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "rapport_statistiques.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        toast.add({
          severity: "success",
          summary: t("common.success_title"),
          detail: "Rapport téléchargé avec succès",
          life: 3000,
        });
      } else {
        throw new Error("No data received");
      }
    } catch (error) {
      console.error("Error downloading report:", error);
      toast.add({
        severity: "error",
        summary: t("error.title"),
        detail: "Erreur lors du téléchargement du rapport",
        life: 3000,
      });
    } finally {
      isDownloading.value = false;
    }
  };

  const downloadReport = async (format, moduleType) => {
    if (activeTab.value === "/user-follow") {
      if (format === "pdf" && moduleType === "awareness") {
        console.log("awareness pdf");
        await downloadReportPdf("awareness");
      } else if (format === "excel" && moduleType === "awareness") {
        console.log("awareness excel");
        await downloadReportExcel(moduleType);
      }
    }

    if (activeTab.value === "/user-follow#pluzz") {
      if (format === "pdf" && moduleType === "pluzz") {
        console.log("pluzz pdf");
        await downloadReportPdf("pluzz");
      } else if (format === "excel" && moduleType === "pluzz") {
        console.log("pluzz excel");
        await downloadReportExcel(moduleType);
      }
    }
  };

  // Initialize data on mount
  onMounted(async () => {
    await refreshData();
  });
  watch(activeTab, async () => {
    await refreshData();
  });

  return {
    // State
    loading,
    isDownloading,
    users,
    usersStats,
    modulePluzzStats,
    searchTerm,
    subscription,
    organization,
    activeTab,
    // Computed
    filteredUsers,

    // Methods
    loadUsers,
    loadUsersStats,
    refreshData,
    formatDate,
    getStatusSeverity,
    retrieveUserSubscription,
    getOrganizationData,
    getUsersStats,
    getModulePluzzStats,
    getMembers,
    buildUserData,
    validatePhoneNumber,
    downloadReport,
  };
}
